import { useId, type ReactNode } from 'react';
import { Button } from '../Button/Button';
import { ModalFooter } from '../Modal/shared/ModalParts';
import { SparkIcon } from '../SparkIcon';
import '../Modal/Modal.css';
import './CookieBanner.css';

export const COOKIE_BANNER_DEFAULT_TITLE = 'We value your privacy';
export const COOKIE_BANNER_DEFAULT_BODY =
  'We use cookies to enhance your experience, analyze site traffic, and deliver personalized content. By clicking "Accept All", you agree to our use of cookies.';

/** Cookie consent card (Figma MVP-DESIGNES 9496:18744 "Modal-Basic"). */
export type CookieBannerProps = {
  /** Header title next to the cookie icon. */
  title?: ReactNode;
  /** Body copy; defaults to the Figma text. */
  children?: ReactNode;
  /** Label of the leading text button. */
  moreInfoLabel?: string;
  /**
   * Where "More info" points. Rendered as a link that opens in a new tab so
   * the person keeps the page (and the undecided banner) they were on.
   */
  moreInfoHref?: string;
  /** Click handler for "More info" when there is no `moreInfoHref`. */
  onMoreInfoClick?: () => void;
  rejectLabel?: string;
  acceptLabel?: string;
  onReject: () => void;
  onAccept: () => void;
  /** Header close (X): hides the banner without recording a choice. */
  onClose: () => void;
  /**
   * `floating` pins the card to the bottom-right corner of the viewport
   * (24px inset, above drawers and modals, below toasts); `inline` renders
   * it in normal flow for docs and layouts that place it themselves.
   */
  placement?: 'floating' | 'inline';
  className?: string;
};

export function CookieBanner({
  title = COOKIE_BANNER_DEFAULT_TITLE,
  children = COOKIE_BANNER_DEFAULT_BODY,
  moreInfoLabel = 'More info',
  moreInfoHref,
  onMoreInfoClick,
  rejectLabel = 'Reject all',
  acceptLabel = 'Accept all',
  onReject,
  onAccept,
  onClose,
  placement = 'floating',
  className = '',
}: CookieBannerProps) {
  const titleId = useId();
  const bodyId = useId();

  const moreInfo = moreInfoHref ? (
    <a
      className="spark-btn spark-btn-medium spark-btn-text"
      href={moreInfoHref}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="spark-btn-label">{moreInfoLabel}</span>
    </a>
  ) : (
    <Button variant="text" size="medium" type="button" onClick={onMoreInfoClick}>
      {moreInfoLabel}
    </Button>
  );

  return (
    <section
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className={[
        'spark-modal',
        'spark-cookie-banner',
        `spark-cookie-banner-${placement}`,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="spark-modal-header">
        <div className="spark-modal-header-leading spark-cookie-banner-leading">
          <SparkIcon slug="cookie" size="medium" className="spark-cookie-banner-icon" />
          <h2 id={titleId} className="spark-modal-header-title">
            {title}
          </h2>
        </div>
        <Button
          variant="ghost"
          size="tiny"
          type="button"
          className="spark-modal-header-close"
          aria-label="Close cookie banner"
          icon={<SparkIcon slug="close" size="small" />}
          onClick={onClose}
        />
      </header>
      <p id={bodyId} className="spark-cookie-banner-body">
        {children}
      </p>
      <ModalFooter
        className="spark-cookie-banner-footer"
        showFooterSlot
        footerSlot={moreInfo}
        tertiaryLabel={rejectLabel}
        primaryLabel={acceptLabel}
        onTertiaryClick={onReject}
        onPrimaryClick={onAccept}
      />
    </section>
  );
}
